import {
  ConsumeSetupTokenSchema,
  ConsumeSimulationTokenSchema,
  SetRegistrationPasswordSchema,
  VerifyRegistrationTotpSchema,
} from "@mind/contracts/browser";
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import { z } from "zod";
import { ApiError, apiRequest } from "../api/client";
import {
  AuthenticatedSchema,
  BrowserSessionSchema,
  PasskeyAuthenticationOptionsSchema,
  PasskeyRegistrationOptionsSchema,
  PasswordSetupResultSchema,
  PasswordSignInResultSchema,
  SetupCompletedSchema,
  SetupStateSchema,
  SetupVerifiedSchema,
  SignedOutSchema,
  SimulationConsumedSchema,
  SimulationEndedSchema,
  TotpSetupMaterialSchema,
  type SetupState,
  type TotpSetupMaterial,
} from "./schemas";
import {
  jsonSafeAuthenticationResponse,
  jsonSafeRegistrationResponse,
} from "./webauthn-response";

const PasswordSignInCommandSchema = z
  .object({
    email: z.string().trim().toLowerCase().email().max(320),
    password: z.string().min(12).max(128),
    rememberMe: z.literal(false),
  })
  .strict();
const SignInTotpSchema = z.object({ code: z.string().regex(/^\d{6}$/), trustDevice: z.literal(false) }).strict();
const SignInBackupCodeSchema = z
  .object({
    code: z.string().trim().min(1).max(100),
    trustDevice: z.literal(false),
    disableSession: z.literal(false),
  })
  .strict();

const AUTH_REQUEST = Object.freeze({
  cache: "no-store" as const,
  credentials: "same-origin" as const,
});

export function canUsePasskeys(): boolean {
  return browserSupportsWebAuthn();
}

export async function getBrowserSession(): Promise<boolean> {
  try {
    const result = await apiRequest(BrowserSessionSchema, "/api/auth/get-session", AUTH_REQUEST);
    return result?.authenticated ?? false;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return false;
    throw error;
  }
}

export async function signOut(): Promise<void> {
  await apiRequest(SignedOutSchema, "/api/auth/sign-out", { ...AUTH_REQUEST, method: "POST" });
}

export async function signInWithPasskey(): Promise<void> {
  const optionsJSON = await apiRequest(
    PasskeyAuthenticationOptionsSchema,
    "/api/auth/passkey/generate-authenticate-options",
    AUTH_REQUEST,
  );
  const credential = jsonSafeAuthenticationResponse(await startAuthentication({ optionsJSON }));
  await apiRequest(AuthenticatedSchema, "/api/auth/passkey/verify-authentication", {
    ...AUTH_REQUEST,
    method: "POST",
    body: { response: credential },
  });
}

export async function signInWithPassword(
  email: string,
  password: string,
): Promise<{ readonly secondFactorRequired: boolean }> {
  const body = PasswordSignInCommandSchema.parse({ email, password, rememberMe: false });
  const result = await apiRequest(PasswordSignInResultSchema, "/api/auth/sign-in/email", {
    ...AUTH_REQUEST,
    method: "POST",
    body,
  });
  return { secondFactorRequired: result.secondFactorRequired };
}

export async function verifySignInTotp(code: string): Promise<void> {
  const body = SignInTotpSchema.parse({ code, trustDevice: false });
  await apiRequest(AuthenticatedSchema, "/api/auth/two-factor/verify-totp", {
    ...AUTH_REQUEST,
    method: "POST",
    body,
  });
}

export async function verifySignInBackupCode(code: string): Promise<void> {
  const body = SignInBackupCodeSchema.parse({ code, trustDevice: false, disableSession: false });
  await apiRequest(AuthenticatedSchema, "/api/auth/two-factor/verify-backup-code", {
    ...AUTH_REQUEST,
    method: "POST",
    body,
  });
}

export async function consumeSetupSecret(token: string): Promise<SetupState> {
  const body = ConsumeSetupTokenSchema.parse({ token });
  return apiRequest(SetupStateSchema, "/api/setup/consume", {
    ...AUTH_REQUEST,
    method: "POST",
    body,
  });
}

export async function readSetupState(): Promise<SetupState> {
  return apiRequest(SetupStateSchema, "/api/setup/context", AUTH_REQUEST);
}

export async function registerSetupPasskey(): Promise<void> {
  const optionsJSON = await apiRequest(
    PasskeyRegistrationOptionsSchema,
    "/api/setup/passkey/options",
    AUTH_REQUEST,
  );
  const credential = jsonSafeRegistrationResponse(await startRegistration({ optionsJSON }));
  await apiRequest(SetupVerifiedSchema, "/api/setup/passkey/verify", {
    ...AUTH_REQUEST,
    method: "POST",
    body: { response: credential },
  });
}

export async function setSetupPassword(password: string): Promise<void> {
  const body = SetRegistrationPasswordSchema.parse({ password });
  await apiRequest(PasswordSetupResultSchema, "/api/setup/password", {
    ...AUTH_REQUEST,
    method: "POST",
    body,
  });
}

export async function issueSetupTotp(): Promise<TotpSetupMaterial> {
  return apiRequest(TotpSetupMaterialSchema, "/api/setup/totp/options", {
    ...AUTH_REQUEST,
    method: "POST",
  });
}

export async function verifySetupTotp(code: string): Promise<void> {
  const body = VerifyRegistrationTotpSchema.parse({ code });
  await apiRequest(SetupCompletedSchema, "/api/setup/totp/verify", {
    ...AUTH_REQUEST,
    method: "POST",
    body,
  });
}

export async function consumeSimulationSecret(token: string) {
  const body = ConsumeSimulationTokenSchema.parse({ token });
  return apiRequest(SimulationConsumedSchema, "/api/simulations/consume", {
    ...AUTH_REQUEST,
    method: "POST",
    body,
  });
}

export async function endSimulation(): Promise<void> {
  await apiRequest(SimulationEndedSchema, "/api/simulations", {
    ...AUTH_REQUEST,
    method: "DELETE",
  });
}
